import {Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {NgxPopupComponent} from "@components/shared/ngx-popups/ngx-popups/components/popup.component";
import {MultiSelectItem} from "@components/shared/popover-menu-multi-select/popover-menu-multi-select.component";
import {DataViewColumn} from "@models/dataview";
import {AutoUnsubscribe} from "@decorators/auto-unsubscribe";
import {Subscription} from "rxjs";
import {HideRowColStack} from "@models/hide-row-col";
import {Period} from "@models/period";

@Component({
    selector: 'app-filter-statements',
    templateUrl: './filter-statements.component.html',
    styleUrls: ['./filter-statements.component.scss'],
    standalone: false
})

@AutoUnsubscribe('subsArr$')
export class FilterStatementsComponent implements OnInit {
  @Input() popup: NgxPopupComponent;
  @Input() columns: DataViewColumn[] = [];
  @Input() hiddenColumnIndices: Set<string>
  @Input() showHiddenDisabled: boolean
  @Input() removeHiddenDisabled: boolean
  @Input() clearUndoDisabled: boolean
  @Input() multiSelectOptionsForReportingInterval: MultiSelectItem[]
  @Input() multiSelectOptionsForYears: MultiSelectItem[]
  @Input() multiSelectOptionsForScenario: MultiSelectItem[]
  @Input() multiSelectOptionsForMonthsCovered: MultiSelectItem[]
  @Input() hideRowColUndoStack: HideRowColStack
  @Input() shouldResetEverything: boolean
  // Full period index (metadata only) and the periods currently selected,
  // so not-yet-loaded periods can be listed alongside loaded columns.
  @Input() periodOptions: Array<Period> = [];
  @Input() selectedPeriods: Set<string> = new Set<string>();
  @Output() callback = new EventEmitter<any>();
  subsArr$: Subscription[] = [];

  HEADER_TEXT: string = "FILTER STATEMENTS";

  multiSelectButtonTextForReportingInterval: string
  multiSelectButtonTextForYears: string
  multiSelectButtonTextForScenarios: string
  multiSelectButtonTextForMonthsCovered: string
  reportingIntervalOptions: string[] = ["Annual", "Semi-Annual", "Monthly", "Quarterly", "Fiscal YTD", "TTM"];
  allAvailableOptions: MultiSelectItem[] = [];
  isAvailableStatementsEmpty: boolean = true
  numberOfAvailableStatementsSelectedText: string
  isFiltering: boolean
  multiSelectOptionsForAvailableStatements: MultiSelectItem[] = [];
  
  // Track statements that user has explicitly unchecked
  private explicitlyUncheckedStatements: Set<string> = new Set<string>();
  
  // Store initial state for cancel functionality
  private initialFilterStates: {
    reportingInterval: boolean[];
    years: boolean[];
    scenario: boolean[];
    monthsCovered: boolean[];
    availableStatements: boolean[];
    hiddenColumnIndices: Set<string>;
  } = null;
  
  get hasFiltersApplied(): boolean {
    return !!(
      this.multiSelectOptionsForReportingInterval?.some(option => option.checked) ||
      this.multiSelectOptionsForYears?.some(option => option.checked) ||
      this.multiSelectOptionsForScenario?.some(option => option.checked) ||
      this.multiSelectOptionsForMonthsCovered?.some(option => option.checked)
    );
  }

  // The dropdowns narrow the list, not the selection - a statement can be
  // selected and absent from the list. These getters name that mismatch.
  get selectedStatementsCount(): number {
    return this.allAvailableOptions ? this.allAvailableOptions.filter(option => option && option.checked).length : 0;
  }

  get hiddenSelectedStatementsCount(): number {
    if (!this.allAvailableOptions || !this.multiSelectOptionsForAvailableStatements) {
      return 0;
    }
    const visibleIds = new Set(this.multiSelectOptionsForAvailableStatements.filter(option => option).map(option => option.id));
    return this.allAvailableOptions.filter(option => option && option.checked && !visibleIds.has(option.id)).length;
  }

  get availableStatementsSummaryText(): string {
    const count = this.selectedStatementsCount;
    const base = count === 1 ? '1 statement selected' : `${count} statements selected`;
    const hidden = this.hiddenSelectedStatementsCount;
    return this.hasFiltersApplied && hidden > 0 ? `${base} · ${hidden} hidden` : base;
  }

  get selectAllMatchingLabel(): string {
    const list = this.multiSelectOptionsForAvailableStatements || [];
    const allChecked = list.length > 0 && list.every(option => option && option.checked);
    const verb = allChecked ? 'Deselect' : 'Select';
    const scope = this.hasFiltersApplied ? ' matching' : '';
    return `${verb} all ${list.length}${scope}`;
  }

  get showFilterMismatchBanner(): boolean {
    return this.hasFiltersApplied && this.hiddenSelectedStatementsCount > 0;
  }

  get filterMismatchBannerText(): string {
    const count = this.hiddenSelectedStatementsCount;
    const noun = count === 1 ? 'selected statement' : 'selected statements';
    const pronoun = count === 1 ? 'It' : 'They';
    return `${count} ${noun} hidden by these filters. ${pronoun} will still show in the summary.`;
  }

  /**
   * Extract year from statementDate string
   * Handles formats like "MM/DD/YYYY"
   */
  private extractYearFromDate(dateString: string): string | null {
    if (!dateString || dateString.length < 4) {
      return null;
    }
    
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        return parts[2];
      }
      if (dateString.length >= 4) {
        return dateString.substring(dateString.length - 4);
      }
    }  
    return null;
  }


  ngOnInit(): void {
    this.isFiltering = false

    if (!this.multiSelectOptionsForScenario) {
      this.multiSelectOptionsForScenario = []
    }

    if (!this.multiSelectOptionsForYears) {
      this.multiSelectOptionsForYears = []
    }

    if (!this.multiSelectOptionsForReportingInterval) {
      this.multiSelectOptionsForReportingInterval = []
    }

    if (!this.multiSelectOptionsForMonthsCovered) {
      this.multiSelectOptionsForMonthsCovered = []
    }

    const savedReportingIntervalByTitle = new Map<string, boolean>();
    if (this.multiSelectOptionsForReportingInterval && this.multiSelectOptionsForReportingInterval.length > 0) {
      this.multiSelectOptionsForReportingInterval.forEach(opt => {
        savedReportingIntervalByTitle.set(opt.title, opt.checked);
      });
    }
    this.multiSelectOptionsForReportingInterval.length = 0;
    this.reportingIntervalOptions.forEach((reportingInterval, idx) => {
      const checked = savedReportingIntervalByTitle.has(reportingInterval)
        ? savedReportingIntervalByTitle.get(reportingInterval)
        : false; // New options (e.g. TTM) not in saved filter: unchecked to preserve prior behavior
      this.multiSelectOptionsForReportingInterval.push({
        id: String(idx),
        title: reportingInterval,
        action: () => this.toggleCheckedStateReportingInterval(this.multiSelectOptionsForReportingInterval[idx]),
        checked,
      });
    });

    const existingYearTitles = new Set<string>();
    if (this.multiSelectOptionsForYears && this.multiSelectOptionsForYears.length > 0) {
      this.multiSelectOptionsForYears.forEach((option, idx) => {
        existingYearTitles.add(option.title);
        option.action = () => this.toggleCheckedStateYears(this.multiSelectOptionsForYears[idx]);
      });
    }
    
    // Find the maximum existing index to continue from
    let maxIdx = 0;
    if (this.multiSelectOptionsForYears && this.multiSelectOptionsForYears.length > 0) {
      const ids = this.multiSelectOptionsForYears
        .map(opt => parseInt(opt.id))
        .filter(id => !isNaN(id));
      if (ids.length > 0) {
        maxIdx = Math.max(...ids) + 1;
      }
    }
    
    for (let i = this.columns.length - 1; i >= 0; i--) { // start at column within most recent year
      const column = this.columns[i];
      const periodLabel = column.statementDate;
      const year = this.extractYearFromDate(periodLabel);
      if (year && !existingYearTitles.has(year)) {
        if (!this.multiSelectOptionsForYears) {
          this.multiSelectOptionsForYears = [];
        }
        const newYearOption: MultiSelectItem = {
          id: String(maxIdx),
          title: year,
          action: () => {},
          checked: false,
        };
        this.multiSelectOptionsForYears.push(newYearOption);
        existingYearTitles.add(year);
        maxIdx++;
      }
    }
    
    if (this.multiSelectOptionsForYears && this.multiSelectOptionsForYears.length > 0) {
      this.multiSelectOptionsForYears.sort((a, b) => {
        const yearA = parseInt(a.title);
        const yearB = parseInt(b.title);
        // Handle invalid years by putting them at the end
        if (isNaN(yearA) && isNaN(yearB)) return 0;
        if (isNaN(yearA)) return 1;
        if (isNaN(yearB)) return -1;
        return yearB - yearA; // Descending order
      });
      this.multiSelectOptionsForYears.forEach((option, idx) => {
        option.action = () => this.toggleCheckedStateYears(this.multiSelectOptionsForYears[idx]);
      });
    }

    if (this.multiSelectOptionsForScenario && this.multiSelectOptionsForScenario.length == 0) {
      const scenarioOptions: string[] = ["Historical", "Projection", "Proforma"];
      scenarioOptions.forEach((scenarioOption, idx) => {
        this.multiSelectOptionsForScenario.push({
          id: String(idx),
          title: scenarioOption,
          action: () => this.toggleCheckedStateReportingScenario(this.multiSelectOptionsForScenario[idx]),
          checked: false,
        });
      });
    } else {
      this.multiSelectOptionsForScenario.forEach((option, idx) => {
        option.action = () => this.toggleCheckedStateReportingScenario(this.multiSelectOptionsForScenario[idx]);
      });
    }

    let monthsIdx = 0;
    if (this.multiSelectOptionsForMonthsCovered && this.multiSelectOptionsForMonthsCovered.length == 0) {
      const uniqueMonths = new Set<number>();
      for (let i = this.columns.length - 1; i >= 0; i--) {
        const column = this.columns[i];
        if (column.monthsInInterval && !uniqueMonths.has(column.monthsInInterval)) {
          uniqueMonths.add(column.monthsInInterval);
          const monthsText = column.monthsInInterval === 1 ? '1 Month' : `${column.monthsInInterval} Months`;
          const option: MultiSelectItem = {
            id: String(monthsIdx),
            title: monthsText,
            action: () => this.toggleCheckedStateMonthsCovered(option),
            checked: false,
          };
          this.multiSelectOptionsForMonthsCovered.push(option);
          monthsIdx++;
        }
      }
      this.multiSelectOptionsForMonthsCovered.sort((a, b) => {
        const aMonths = parseInt(a.title.split(' ')[0]);
        const bMonths = parseInt(b.title.split(' ')[0]);
        return aMonths - bMonths;
      });
      this.updateMultiSelectButtonTextForMonthsCovered();
    } else {
      if (this.multiSelectOptionsForMonthsCovered && this.multiSelectOptionsForMonthsCovered.length > 0) {
        this.multiSelectOptionsForMonthsCovered.forEach((option) => {
          option.action = () => this.toggleCheckedStateMonthsCovered(option);
        });
      }
      this.updateMultiSelectButtonTextForMonthsCovered();
    }

    this.initializeAllAvailableOptions()
    this.explicitlyUncheckedStatements.clear();

    // Checked state is set at creation time in initializeAllAvailableOptions.
    // Just record which ones started off unchecked.
    this.allAvailableOptions.forEach(option => {
      const key = `${option.title || ''}|${option.subtitle1 || ''}|${option.subtitle2 || ''}|${option.subtitle3 || ''}`;
      if (!option.checked) {
        this.explicitlyUncheckedStatements.add(key);
      } else {
        this.explicitlyUncheckedStatements.delete(key);
      }
    })

    this.filterAvailableStatements()
    if (this.shouldResetEverything) {
      this.resetFilters()
    }

    this.isAvailableStatementsEmpty = this.multiSelectOptionsForAvailableStatements.length <= 0;
    this.updateMultiSelectButtonTextForReportingInterval()
    this.updateMultiSelectButtonTextForYears()
    this.updateMultiSelectButtonTextForScenario()
    this.updateNumberOfAvailableStatementsSelected()
    
    // Store initial state for cancel functionality
    this.storeInitialState()
  }
  
  /**
   * Store the initial state of all filters for cancel functionality
   */
  private storeInitialState(): void {
    // Store initial checked states for all filters and available statements
    // Store from allAvailableOptions (not filtered list) to preserve all statement states
    this.initialFilterStates = {
      reportingInterval: this.multiSelectOptionsForReportingInterval 
        ? this.multiSelectOptionsForReportingInterval.map(opt => opt.checked) 
        : [],
      years: this.multiSelectOptionsForYears 
        ? this.multiSelectOptionsForYears.map(opt => opt.checked) 
        : [],
      scenario: this.multiSelectOptionsForScenario 
        ? this.multiSelectOptionsForScenario.map(opt => opt.checked) 
        : [],
      monthsCovered: this.multiSelectOptionsForMonthsCovered 
        ? this.multiSelectOptionsForMonthsCovered.map(opt => opt.checked) 
        : [],
      availableStatements: this.allAvailableOptions 
        ? this.allAvailableOptions.map(opt => opt.checked) 
        : [],
      hiddenColumnIndices: this.hiddenColumnIndices 
        ? new Set(this.hiddenColumnIndices) 
        : new Set<string>()
    };
  }
  
  /**
   * Restore the initial state of all filters (used when Cancel is clicked)
   */
  private restoreInitialState(): void {
    if (!this.initialFilterStates) {
      return;
    }
    
    // Restore reporting interval filters
    if (this.multiSelectOptionsForReportingInterval && this.initialFilterStates.reportingInterval.length > 0) {
      this.multiSelectOptionsForReportingInterval.forEach((opt, idx) => {
        if (idx < this.initialFilterStates.reportingInterval.length) {
          opt.checked = this.initialFilterStates.reportingInterval[idx];
        }
      });
    }
    
    // Restore years filters
    if (this.multiSelectOptionsForYears && this.initialFilterStates.years.length > 0) {
      this.multiSelectOptionsForYears.forEach((opt, idx) => {
        if (idx < this.initialFilterStates.years.length) {
          opt.checked = this.initialFilterStates.years[idx];
        }
      });
    }
    
    // Restore scenario filters
    if (this.multiSelectOptionsForScenario && this.initialFilterStates.scenario.length > 0) {
      this.multiSelectOptionsForScenario.forEach((opt, idx) => {
        if (idx < this.initialFilterStates.scenario.length) {
          opt.checked = this.initialFilterStates.scenario[idx];
        }
      });
    }
    
    // Restore months covered filters
    if (this.multiSelectOptionsForMonthsCovered && this.initialFilterStates.monthsCovered.length > 0) {
      this.multiSelectOptionsForMonthsCovered.forEach((opt, idx) => {
        if (idx < this.initialFilterStates.monthsCovered.length) {
          opt.checked = this.initialFilterStates.monthsCovered[idx];
        }
      });
    }
    
    // Restore available statements from allAvailableOptions (not filtered list)
    // This ensures we restore the state of all statements, not just visible ones
    if (this.allAvailableOptions && this.initialFilterStates.availableStatements.length > 0) {
      this.allAvailableOptions.forEach((opt, idx) => {
        if (opt && idx < this.initialFilterStates.availableStatements.length) {
          opt.checked = this.initialFilterStates.availableStatements[idx];
        }
      });
    }
    
    // Clear explicitlyUncheckedStatements to match restored state
    // We'll rebuild it based on restored checked states if needed
    this.explicitlyUncheckedStatements.clear();
    if (this.allAvailableOptions) {
      this.allAvailableOptions.forEach(opt => {
        if (opt && !opt.checked) {
          const key = `${opt.title || ''}|${opt.subtitle1 || ''}|${opt.subtitle2 || ''}|${opt.subtitle3 || ''}`;
          this.explicitlyUncheckedStatements.add(key);
        }
      });
    }
    
    // Restore hidden column indices
    if (this.hiddenColumnIndices && this.initialFilterStates.hiddenColumnIndices) {
      this.hiddenColumnIndices.clear();
      this.initialFilterStates.hiddenColumnIndices.forEach(value => {
        this.hiddenColumnIndices.add(value);
      });
    }
    
    // Update button texts and filter available statements
    // This will rebuild the filtered list based on restored filter states
    this.filterAvailableStatements();
    this.updateMultiSelectButtonTextForReportingInterval();
    this.updateMultiSelectButtonTextForYears();
    this.updateMultiSelectButtonTextForScenario();
    this.updateMultiSelectButtonTextForMonthsCovered();
    this.updateNumberOfAvailableStatementsSelected();
  }

  // One option per loaded column, plus one per not-yet-loaded period
  // (tagged "will be loaded", checked from selectedPeriods since there's
  // no column yet to read visibility from).
  initializeAllAvailableOptions() {
    let idx = 0;
    const seenPeriodValues = new Set<string>();

    for (let i = this.columns.length - 1; i >= 0; i--) { // start at most recent column
      const column = this.columns[i];
      const option = this.buildOptionFromColumn(column, idx);
      this.allAvailableOptions.push(option);
      seenPeriodValues.add(column.reportingPeriod);
      idx++;
    }

    if (this.periodOptions && this.periodOptions.length) {
      for (let i = this.periodOptions.length - 1; i >= 0; i--) { // most recent first
        const period = this.periodOptions[i];
        if (seenPeriodValues.has(period.value)) {
          continue; // already represented by a loaded column
        }
        const option = this.buildOptionFromPeriod(period, idx);
        this.allAvailableOptions.push(option);
        seenPeriodValues.add(period.value);
        idx++;
      }
    }
  }

  private buildOptionFromColumn(column: DataViewColumn, idx: number): MultiSelectItem {
    const capitalizedReportingInterval = this.getEquivalentParsed(column.reportingInterval);
    const correctedPreparationType = this.getEquivalentParsed(column.preparationType);
    let scenario = column.scenario.charAt(0).toUpperCase() + column.scenario.slice(1).toLowerCase()
    if (column.scenario.toLowerCase() === "projection" || column.scenario.toLowerCase() === "proforma") {
      scenario += " (" + column.projectionName + ")"
    }
    const option: MultiSelectItem = {
      id: String(idx),
      title: column.statementDate,
      subtitle1: scenario,
      subtitle2: correctedPreparationType,
      subtitle3: capitalizedReportingInterval,
      periodValue: column.reportingPeriod,
      willBeLoaded: false,
      action: () => {},
      checked: !column.isHidden,
    };
    option.action = () => this.toggleCheckedStateAvailableStatements(this.allAvailableOptions.find(item => item.id === option.id));
    return option;
  }

  private buildOptionFromPeriod(period: Period, idx: number): MultiSelectItem {
    const capitalizedReportingInterval = this.getEquivalentParsed(period.interval);
    const correctedPreparationType = this.getEquivalentParsed(period.preparationType);
    const scenario = (period.scenario || '').charAt(0).toUpperCase() + (period.scenario || '').slice(1).toLowerCase();
    const option: MultiSelectItem = {
      id: String(idx),
      title: period.label,
      subtitle1: scenario,
      subtitle2: correctedPreparationType,
      subtitle3: capitalizedReportingInterval,
      periodValue: period.value,
      willBeLoaded: true,
      action: () => {},
      checked: !!(this.selectedPeriods && this.selectedPeriods.has(period.value)),
    };
    option.action = () => this.toggleCheckedStateAvailableStatements(this.allAvailableOptions.find(item => item.id === option.id));
    return option;
  }

  resetFilters() {
    this.resetReportingIntervalFilter()
    this.resetYearsFilter()
    this.resetScenarioFilter()
    this.resetMonthsCoveredFilter()

    this.updateMultiSelectButtonTextForScenario()
    this.updateMultiSelectButtonTextForReportingInterval()
    this.updateMultiSelectButtonTextForYears()
    this.updateMultiSelectButtonTextForMonthsCovered()

    // Reset all statements to checked and show all
    this.selectAllStatements()
    // Filter available statements (will show all checked statements since filters are reset)
    this.filterAvailableStatements()
    this.updateNumberOfAvailableStatementsSelected()
  }

  selectAllStatements() {
    // First, sync current filtered list states back to allAvailableOptions
    if (this.multiSelectOptionsForAvailableStatements) {
      this.multiSelectOptionsForAvailableStatements.forEach(option => {
        if (option) {
          const sourceOption = this.allAvailableOptions.find(opt => 
            opt && option && (
              opt.id === option.id ||
              (opt.title === option.title && 
               opt.subtitle1 === option.subtitle1 && 
               opt.subtitle2 === option.subtitle2 && 
               opt.subtitle3 === option.subtitle3)
            )
          );
          if (sourceOption) {
            sourceOption.checked = option.checked;
          }
        }
      });
    }
    
    // Check all statements in the currently visible filtered list
    if (this.multiSelectOptionsForAvailableStatements) {
      this.multiSelectOptionsForAvailableStatements.forEach(option => {
        if (option) {
          option.checked = true;
          // Update explicitlyUncheckedStatements tracking
          const key = `${option.title || ''}|${option.subtitle1 || ''}|${option.subtitle2 || ''}|${option.subtitle3 || ''}`;
          this.explicitlyUncheckedStatements.delete(key);
        }
      });
    }
    
    // Also check all statements in allAvailableOptions that match current filters
    // This ensures statements that are currently filtered out but match filters are also checked
    if (this.allAvailableOptions) {
      this.allAvailableOptions.forEach(option => {
        if (option) {
          option.checked = true;
          const key = `${option.title || ''}|${option.subtitle1 || ''}|${option.subtitle2 || ''}|${option.subtitle3 || ''}`;
          this.explicitlyUncheckedStatements.delete(key);
        }
      });
    }
    
    // Re-apply filters to update the visible list
    this.filterAvailableStatements();
  }

  resetReportingIntervalFilter() {
    this.multiSelectOptionsForReportingInterval.forEach(option => {
      option.checked = false
    })
  }

  resetYearsFilter() {
    this.multiSelectOptionsForYears.forEach(option => {
      option.checked = false
    })
  }

  resetScenarioFilter() {
    this.multiSelectOptionsForScenario.forEach(option => {
      option.checked = false
    })
  }

  resetMonthsCoveredFilter() {
    this.multiSelectOptionsForMonthsCovered.forEach(option => {
      option.checked = false
    })
  }

  filterAvailableStatements() {
    // Sync checked states from current filtered list back to allAvailableOptions before filtering
    // This ensures we preserve user selections when filters change
    if (this.multiSelectOptionsForAvailableStatements && 
        this.multiSelectOptionsForAvailableStatements.length > 0 &&
        this.multiSelectOptionsForAvailableStatements !== this.allAvailableOptions) {
      this.multiSelectOptionsForAvailableStatements.forEach(filteredOption => {
        if (!filteredOption) {
          return;
        }
        const sourceOption = this.allAvailableOptions.find(opt => 
          opt && filteredOption && (
            opt.id === filteredOption.id ||
            (opt.title === filteredOption.title && 
             opt.subtitle1 === filteredOption.subtitle1 && 
             opt.subtitle2 === filteredOption.subtitle2 && 
             opt.subtitle3 === filteredOption.subtitle3)
          )
        );
        if (sourceOption) {
          sourceOption.checked = filteredOption.checked;
        }
      });
    }

    const reportingIntervalFilter: string[] = [];
    const yearFilter: string[] = [];
    const scenarioFilter: string[] = [];
    const monthsCoveredFilter: number[] = [];

    // Build filter arrays from checked options
    if (this.multiSelectOptionsForReportingInterval) {
      this.multiSelectOptionsForReportingInterval.forEach(option => {
        if (option && option.checked) {
          reportingIntervalFilter.push(option.title);
        }
      });
    }

    if (this.multiSelectOptionsForYears) {
      this.multiSelectOptionsForYears.forEach(option => {
        if (option && option.checked) {
          yearFilter.push(option.title);
        }
      });
    }

    if (this.multiSelectOptionsForScenario) {
      this.multiSelectOptionsForScenario.forEach(option => {
        if (option && option.checked) {
          scenarioFilter.push(option.title);
        }
      });
    }

    if (this.multiSelectOptionsForMonthsCovered) {
      this.multiSelectOptionsForMonthsCovered.forEach(option => {
        if (option && option.checked && option.title) {
          const monthsValue = parseInt(option.title.split(' ')[0]);
          if (!isNaN(monthsValue)) {
            monthsCoveredFilter.push(monthsValue);
          }
        }
      });
    }

    const hasAnyFilter = reportingIntervalFilter.length > 0 || yearFilter.length > 0 || scenarioFilter.length > 0 || monthsCoveredFilter.length > 0;

    // Filter available statements to only show checked statements that match selected filters
    if (hasAnyFilter) {
      this.multiSelectOptionsForAvailableStatements = this.allAvailableOptions.filter(option => {
        if (!option) {
          return false;
        }

        // Extract year from statementDate and check if it matches the filter
        let matchesYear = yearFilter.length === 0;
        if (yearFilter.length > 0 && option.title) {
          const year = this.extractYearFromDate(option.title);
          if (year) {
            matchesYear = yearFilter.includes(year);
          } else {
            matchesYear = false;
          }
        }

        // Check reporting interval match
        let matchesReportingInterval = reportingIntervalFilter.length === 0;
        if (reportingIntervalFilter.length > 0 && option.subtitle3) {
          const parsedInterval = this.getEquivalentParsed(option.subtitle3);
          // Case-insensitive comparison for reporting interval matching
          matchesReportingInterval = reportingIntervalFilter.some(filter => 
            filter && parsedInterval && 
            filter.toLowerCase() === parsedInterval.toLowerCase()
          );
        }

        // Check scenario match (handle projection/proforma with names)
        let matchesScenario = scenarioFilter.length === 0;
        if (scenarioFilter.length > 0 && option.subtitle1) {
          const scenarioFirstWord = option.subtitle1.split(" ")[0];
          // Case-insensitive comparison for scenario matching
          matchesScenario = scenarioFilter.some(filter => 
            filter && scenarioFirstWord && 
            filter.toLowerCase() === scenarioFirstWord.toLowerCase()
          );
        }

        // Check months covered match
        let matchesMonthsCovered = monthsCoveredFilter.length === 0;
        if (monthsCoveredFilter.length > 0) {
          const column = this.columns.find(col => {
            if (!col || !col.statementDate || !col.scenario || !col.preparationType || !col.reportingInterval) {
              return false;
            }
            let capitalizedReportingInterval = this.getEquivalentParsed(col.reportingInterval);
            let correctedPreparationType = this.getEquivalentParsed(col.preparationType);
            let scenario = col.scenario.charAt(0).toUpperCase() + col.scenario.slice(1).toLowerCase();
            if (col.scenario.toLowerCase() === "projection" || col.scenario.toLowerCase() === "proforma") {
              scenario += " (" + (col.projectionName || "") + ")";
            }
            
            const statementDateMatch = col.statementDate.toLowerCase() === option.title.toLowerCase();
            const scenarioMatch = scenario === option.subtitle1;
            const preparationTypeMatch = correctedPreparationType.toLowerCase() === option.subtitle2.toLowerCase();
            const reportingIntervalMatch = capitalizedReportingInterval.toLowerCase() === option.subtitle3.toLowerCase();
            
            return statementDateMatch && scenarioMatch && preparationTypeMatch && reportingIntervalMatch;
          });
          
          if (column && column.monthsInInterval != null && typeof column.monthsInInterval === 'number') {
            matchesMonthsCovered = monthsCoveredFilter.includes(Number(column.monthsInInterval));
          } else {
            matchesMonthsCovered = false;
          }
        }

        // Check if statement matches all selected filters
        const matchesAllFilters = matchesYear && matchesReportingInterval && matchesScenario && matchesMonthsCovered;
        
        // If statement doesn't match filters, hide it
        if (!matchesAllFilters) {
          return false; // Hide statements that don't match filters
        }
        
        // Statement matches filters - show it regardless of checked state
        // Don't auto-check here - preserve the current checked state
        // The checked state should only change when user explicitly checks/unchecks
        // or when filters are cleared (no filters selected)
        
        // Show all statements that match filters (both checked and unchecked)
        return true;
      });
      
      // Update action callbacks to reference the correct option in the filtered array
      this.multiSelectOptionsForAvailableStatements.forEach((option, idx) => {
        if (option) {
          option.action = () => this.toggleCheckedStateAvailableStatements(this.multiSelectOptionsForAvailableStatements[idx]);
        }
      });
    } else {
      if (this.allAvailableOptions) {
        this.allAvailableOptions.forEach(option => {
          if (option) {
            const key = `${option.title || ''}|${option.subtitle1 || ''}|${option.subtitle2 || ''}|${option.subtitle3 || ''}`;
            if (!this.explicitlyUncheckedStatements.has(key)) {
              option.checked = true;
            } else {
              option.checked = false;
            }
          }
        });
      }
      
      this.multiSelectOptionsForAvailableStatements = this.allAvailableOptions.filter(option => {
        return option && option !== null;
      });
      
      // Update action callbacks to reference the correct option in the filtered array
      this.multiSelectOptionsForAvailableStatements.forEach((option, idx) => {
        if (option) {
          option.action = () => this.toggleCheckedStateAvailableStatements(this.multiSelectOptionsForAvailableStatements[idx]);
        }
      });
    }

    this.updateNumberOfAvailableStatementsSelected();
    this.isAvailableStatementsEmpty = this.multiSelectOptionsForAvailableStatements.length <= 0;
  }

  close(restoreState: boolean = false, selectedPeriods?: Set<string>) {
    // Restore initial state before closing if this is a cancel action
    if (restoreState) {
      this.restoreInitialState();
    }

    if (this.hiddenColumnIndices.size > 0) {
      this.showHiddenDisabled = false
      this.removeHiddenDisabled = false
      this.clearUndoDisabled = false
      this.hiddenColumnIndices.forEach(column => {
        this.hideRowColUndoStack.pushColumn(true, column)
      })
    } else {
      this.showHiddenDisabled = true
      this.removeHiddenDisabled = true
      this.clearUndoDisabled = true

    }
    this.callback.emit({
      hiddenColumnsPassedIn: this.hiddenColumnIndices,
      reportingIntervalFilters: this.multiSelectOptionsForReportingInterval,
      yearsFilter: this.multiSelectOptionsForYears,
      scenarioFilter: this.multiSelectOptionsForScenario,
      monthsCoveredFilter: this.multiSelectOptionsForMonthsCovered,
      showHiddenDisabled: this.showHiddenDisabled,
      removeHiddenDisabled: this.removeHiddenDisabled,
      clearUndoDisabled: this.clearUndoDisabled,
      hideRowColUndoStack: this.hideRowColUndoStack,
      selectedPeriods
    });
    if (this.popup) {
      this.popup.close();
    }
  }

  toggleCheckedStateAvailableStatements(option) {
    if (!option) {
      return;
    }
    // Toggle the checked state - since filtered array contains references to allAvailableOptions objects,
    // this will update both the filtered view and the source
    option.checked = !option.checked;
    
    // Track explicitly unchecked statements
    const key = `${option.title || ''}|${option.subtitle1 || ''}|${option.subtitle2 || ''}|${option.subtitle3 || ''}`;
    if (option.checked === false) {
      this.explicitlyUncheckedStatements.add(key);
    } else {
      this.explicitlyUncheckedStatements.delete(key);
    }
    
    // Also update in allAvailableOptions to ensure state is preserved
    const sourceOption = this.allAvailableOptions.find(opt => 
      opt && option && (
        opt.id === option.id ||
        (opt.title === option.title && 
         opt.subtitle1 === option.subtitle1 && 
         opt.subtitle2 === option.subtitle2 && 
         opt.subtitle3 === option.subtitle3)
      )
    );
    if (sourceOption) {
      sourceOption.checked = option.checked;
    }
    // Re-filter to update the visible list (hide unchecked, show checked)
    this.filterAvailableStatements();
  }

  toggleCheckedStateYears(option) {
    option.checked = !option.checked
    this.filterAvailableStatements()
    this.updateMultiSelectButtonTextForYears()
  }

  toggleCheckedStateReportingInterval(option) {
    option.checked = !option.checked
    this.filterAvailableStatements()
    this.updateMultiSelectButtonTextForReportingInterval()
  }

  toggleCheckedStateReportingScenario(option) {
    option.checked = !option.checked
    this.filterAvailableStatements()
    this.updateMultiSelectButtonTextForScenario()
  }

  toggleCheckedStateMonthsCovered(option) {
    option.checked = !option.checked
    this.filterAvailableStatements()
    this.updateMultiSelectButtonTextForMonthsCovered()
  }


  handleSelectOrDeselectAll(values: any[]) {
    // Determine if this is for available statements or filter dropdowns
    const isAvailableStatements = values === this.multiSelectOptionsForAvailableStatements;
    
    if (isAvailableStatements) {
      const allVisibleChecked = values && values.length > 0 && values.every(option => option && option.checked === true);

      const shouldCheckAll = !allVisibleChecked;

      // Scope to the currently-narrowed ("N matching") list, not every
      // available statement. values' options are the same object references
      // as in allAvailableOptions (built via .filter(), not cloned), so
      // mutating them here is enough - no separate sync-back needed.
      if (values && values.length > 0) {
        values.forEach(option => {
          if (!option) {
            return;
          }
          option.checked = shouldCheckAll;

          const key = `${option.title || ''}|${option.subtitle1 || ''}|${option.subtitle2 || ''}|${option.subtitle3 || ''}`;
          if (option.checked === false) {
            this.explicitlyUncheckedStatements.add(key);
          } else {
            this.explicitlyUncheckedStatements.delete(key);
          }
        });
      }
      this.filterAvailableStatements();
      this.updateMultiSelectButtonTextForReportingInterval();
      this.updateMultiSelectButtonTextForYears();
      this.updateMultiSelectButtonTextForScenario();
      this.updateMultiSelectButtonTextForMonthsCovered();
      this.updateNumberOfAvailableStatementsSelected();
      return;
    }
    
    const shouldCheckAll = !values.some(option => option && option.checked === true);
    
    values.forEach(option => {
      if (option) {
        option.checked = shouldCheckAll;
      }
    });
    
    // Re-apply filters to update the visible list
    this.filterAvailableStatements();
    
    // Update UI text for filter buttons
    this.updateMultiSelectButtonTextForReportingInterval();
    this.updateMultiSelectButtonTextForYears();
    this.updateMultiSelectButtonTextForScenario();
    this.updateMultiSelectButtonTextForMonthsCovered();
    this.updateNumberOfAvailableStatementsSelected();
  }

  areAllPeriodsChecked(): boolean {
    if (!this.multiSelectOptionsForAvailableStatements || !Array.isArray(this.multiSelectOptionsForAvailableStatements) || this.multiSelectOptionsForAvailableStatements.length === 0) {
      return false;
    }
    return this.multiSelectOptionsForAvailableStatements.every(item => item && item.checked === true);
  }


  getEquivalentParsed(reportingInterval: string): string {
    if (!reportingInterval || typeof reportingInterval !== 'string') {
      return '';
    }
    if (reportingInterval.trim().toUpperCase() === 'TTM') {
      return 'TTM';
    }
    const words: string[] = reportingInterval.toLowerCase().split("_");
    if (words.length > 1 && words[1]) {
      words[1] = words[1].charAt(0).toUpperCase() + words[1].slice(1);
    }
    let capitalizedReportingInterval: string = words.join("-");
    if (capitalizedReportingInterval && capitalizedReportingInterval.length > 0) {
      capitalizedReportingInterval = capitalizedReportingInterval.charAt(0).toUpperCase() + capitalizedReportingInterval.slice(1);
    }

    if (capitalizedReportingInterval === ("Annually")) {
      capitalizedReportingInterval = "Annual"
    } else if (capitalizedReportingInterval.toLowerCase().includes("annually")) {
      capitalizedReportingInterval = "Semi-Annual"
    } else if (capitalizedReportingInterval.toLowerCase().includes("fiscal")) {
      capitalizedReportingInterval = "Fiscal YTD"
    }
    return capitalizedReportingInterval || '';
  }

  filterStatements() {
    this.isFiltering = true

    // Build filter arrays from checked filter options (same logic as filterAvailableStatements)
    const reportingIntervalFilter: string[] = [];
    const yearFilter: string[] = [];
    const scenarioFilter: string[] = [];
    const monthsCoveredFilter: number[] = [];

    if (this.multiSelectOptionsForReportingInterval) {
      this.multiSelectOptionsForReportingInterval.forEach(option => {
        if (option && option.checked) {
          reportingIntervalFilter.push(option.title);
        }
      });
    }

    if (this.multiSelectOptionsForYears) {
      this.multiSelectOptionsForYears.forEach(option => {
        if (option && option.checked) {
          yearFilter.push(option.title);
        }
      });
    }

    if (this.multiSelectOptionsForScenario) {
      this.multiSelectOptionsForScenario.forEach(option => {
        if (option && option.checked) {
          scenarioFilter.push(option.title);
        }
      });
    }

    if (this.multiSelectOptionsForMonthsCovered) {
      this.multiSelectOptionsForMonthsCovered.forEach(option => {
        if (option && option.checked && option.title) {
          const monthsValue = parseInt(option.title.split(' ')[0]);
          if (!isNaN(monthsValue)) {
            monthsCoveredFilter.push(monthsValue);
          }
        }
      });
    }

    const hasAnyFilter = reportingIntervalFilter.length > 0 || yearFilter.length > 0 || scenarioFilter.length > 0 || monthsCoveredFilter.length > 0;

    this.columns.forEach(column => {
      const strIdx = column.toKeyString()
      let matchingOption = null;

      // Prepare column data for matching (used for both finding matchingOption and filter matching)
      let scenario = column.scenario.charAt(0).toUpperCase() + column.scenario.slice(1).toLowerCase()
      if (column.scenario.toLowerCase() === "projection" || column.scenario.toLowerCase() === "proforma") {
        scenario += " (" + column.projectionName + ")"
      }
      let capitalizedReportingInterval = this.getEquivalentParsed(column.reportingInterval);
      let correctedPreparationType = this.getEquivalentParsed(column.preparationType);

      if (this.allAvailableOptions && this.allAvailableOptions.length > 0) {
        matchingOption = this.allAvailableOptions.find(option => {
          if (!option) return false;
          return column.statementDate.toLowerCase() === option.title.toLowerCase() && 
                 scenario === option.subtitle1 &&
                 correctedPreparationType.toLowerCase() === option.subtitle2.toLowerCase() && 
                 capitalizedReportingInterval.toLowerCase() === option.subtitle3.toLowerCase();
        });
      }

      // Check if column matches filter criteria
      let matchesFilters = true;
      if (hasAnyFilter) {
        // Extract year from statementDate and check if it matches the filter
        let matchesYear = yearFilter.length === 0;
        if (yearFilter.length > 0 && column.statementDate) {
          const year = this.extractYearFromDate(column.statementDate);
          if (year) {
            matchesYear = yearFilter.includes(year);
          } else {
            matchesYear = false;
          }
        }

        // Check reporting interval match
        let matchesReportingInterval = reportingIntervalFilter.length === 0;
        if (reportingIntervalFilter.length > 0 && capitalizedReportingInterval) {
          matchesReportingInterval = reportingIntervalFilter.some(filter => 
            filter && capitalizedReportingInterval && 
            filter.toLowerCase() === capitalizedReportingInterval.toLowerCase()
          );
        }

        // Check scenario match
        let matchesScenario = scenarioFilter.length === 0;
        if (scenarioFilter.length > 0 && scenario) {
          const scenarioFirstWord = scenario.split(" ")[0];
          matchesScenario = scenarioFilter.some(filter => 
            filter && scenarioFirstWord && 
            filter.toLowerCase() === scenarioFirstWord.toLowerCase()
          );
        }

        // Check months covered match
        let matchesMonthsCovered = monthsCoveredFilter.length === 0;
        if (monthsCoveredFilter.length > 0 && column.monthsInInterval != null && typeof column.monthsInInterval === 'number') {
          matchesMonthsCovered = monthsCoveredFilter.includes(Number(column.monthsInInterval));
        }

        matchesFilters = matchesYear && matchesReportingInterval && matchesScenario && matchesMonthsCovered;
      }

      // Show column only if:
      // 1. It matches the filter criteria (if filters are selected)
      // 2. AND it's checked in allAvailableOptions
      if (matchingOption && matchesFilters && matchingOption.checked) {
        // Show the column
        if (this.hiddenColumnIndices.has(strIdx)) {
          this.hiddenColumnIndices.delete(strIdx);
          column.isHidden = false
        }
      } else {
        // Hide the column (either doesn't match filters, not checked, or no matching option)
        if (!this.hiddenColumnIndices.has(strIdx)) {
          this.hiddenColumnIndices.add(strIdx);
          column.isHidden = true
        }
      }
    })
    
    if (this.hiddenColumnIndices.size > 0) {
      this.showHiddenDisabled = false
      this.removeHiddenDisabled = false
      this.clearUndoDisabled = false
    }

    // Commit every checked period, loaded or not, regardless of the dropdowns.
    const newSelectedPeriods = new Set<string>(
      this.allAvailableOptions
        .filter(option => option && option.checked && option.periodValue)
        .map(option => option.periodValue)
    );

    this.close(false, newSelectedPeriods);
  }

  updateMultiSelectButtonTextForReportingInterval() {
    const countSelectedPeriods = this.multiSelectOptionsForReportingInterval.reduce((count, option) => count + (option.checked === true ? 1 : 0), 0);
    if (countSelectedPeriods < 1) {
      this.multiSelectButtonTextForReportingInterval = 'Select'
    } else if (countSelectedPeriods === 1) {
      this.multiSelectButtonTextForReportingInterval = "1 interval selected";
    } else {
      this.multiSelectButtonTextForReportingInterval = String(countSelectedPeriods) + " intervals selected";
    }
  }

  updateMultiSelectButtonTextForYears() {
    const countSelectedPeriods = this.multiSelectOptionsForYears.reduce((count, option) => count + (option.checked === true ? 1 : 0), 0);

    if (countSelectedPeriods < 1) {
      this.multiSelectButtonTextForYears = 'Select'
    } else if (countSelectedPeriods === 1) {
      this.multiSelectButtonTextForYears = "1 year selected";
    } else {
      this.multiSelectButtonTextForYears = String(countSelectedPeriods) + " years selected";
    }
  }

  updateMultiSelectButtonTextForScenario() {
    const countSelectedPeriods = this.multiSelectOptionsForScenario.reduce((count, option) => count + (option.checked === true ? 1 : 0), 0);
    if (countSelectedPeriods < 1) {
      this.multiSelectButtonTextForScenarios = 'Select'
    } else if (countSelectedPeriods === 1) {
      this.multiSelectButtonTextForScenarios = "1 scenario selected";
    } else {
      this.multiSelectButtonTextForScenarios = String(countSelectedPeriods) + " scenarios selected";
    }
  }

  updateMultiSelectButtonTextForMonthsCovered() {
    const countSelectedPeriods = this.multiSelectOptionsForMonthsCovered.reduce((count, option) => count + (option.checked === true ? 1 : 0), 0);
    if (countSelectedPeriods < 1) {
      this.multiSelectButtonTextForMonthsCovered = 'Select'
    } else if (countSelectedPeriods === 1) {
      this.multiSelectButtonTextForMonthsCovered = "1 month selected";
    } else {
      this.multiSelectButtonTextForMonthsCovered = String(countSelectedPeriods) + " months selected";
    }
  }

  updateNumberOfAvailableStatementsSelected() {
    this.numberOfAvailableStatementsSelectedText = this.availableStatementsSummaryText;
  }
}

